/* Has this character's hit point MAXIMUM been changed, and which way?
 *
 * A leftover test drain went unnoticed into a played session on 2026-09-28. The
 * data was never wrong — maxHpModifier is signed and maxHpBase holds the original
 * — it was shown only as a muted "mod -10" inside the expanded detail panel, so
 * the number that changes what every heal and every death save means was one
 * click away from the DM's eye.
 *
 * Both directions are reported, not just drains. The model signs the modifier, so
 * Aid reads +10 and Life Drain reads -10; showing only the bad one would mean the
 * mark's ABSENCE stopped meaning "unmodified", which is the fault being fixed.
 */
import type { Character } from "./types";

export type MaxHpMark =
  | { kind: "none" }
  | { kind: "drained" | "boosted"; delta: number; base: number; max: number; label: string; title: string };

/** The original maximum, before anything modified it. */
export const maxHpBaseOf = (c: Pick<Character, "maxHp" | "maxHpBase" | "maxHpModifier">): number => {
  const base = c.maxHpBase;
  if (base !== undefined && Number.isFinite(Number(base))) return Number(base);
  return Number(c.maxHp || 0) - Number(c.maxHpModifier || 0);
};

export function maxHpMark(c: Pick<Character, "maxHp" | "maxHpBase" | "maxHpModifier">): MaxHpMark {
  const delta = Number(c.maxHpModifier || 0);
  /* A non-finite modifier is not a modifier. NaN would otherwise render as a chip
     reading "max HP NaN", which is worse than no chip: it looks like a real state. */
  if (!Number.isFinite(delta) || delta === 0) return { kind: "none" };
  const max = Number(c.maxHp || 0);
  const base = maxHpBaseOf(c);
  const kind = delta < 0 ? "drained" : "boosted";
  const sign = delta < 0 ? "−" : "+";           // U+2212, not a hyphen: it reads as a number at small sizes
  const label = `max HP ${sign}${Math.abs(delta)}`;
  const title = delta < 0
    ? `Hit point maximum reduced: ${base} normally, ${max} now. Healing cannot go above ${max}, and this persists until something restores it (a long rest, greater restoration).`
    : `Hit point maximum raised: ${base} normally, ${max} now. Aid and the like. It ends when the spell does.`;
  return { kind, delta, base, max, label, title };
}
